import { Modal, Form, Select, Input, message } from 'antd';
import AxiosOICStat from "AxiosOICStat";
import { useEffect, useState, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';

const { Option } = Select;

const WhoIndicatorMappingModal = ({ visible, onCancel, onSuccess, record }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [whoItems, setWhoItems] = useState([]);
  const [whoSearch, setWhoSearch] = useState('');
  const searchTimer = useRef(null);
  const indicatorsState = useSelector((state) => state.indicators);

  const availableIndicators = indicatorsState.value
    ? Object.values(indicatorsState.value).sort((a, b) => (a.ind_name_eng || '').localeCompare(b.ind_name_eng || ''))
    : [];

  const indicatorOptions = useMemo(() => {
    const options = availableIndicators.map(ind => ({
      key: String(ind.ind_code),
      value: String(ind.ind_code),
      label: `${ind.ind_code} - ${ind.ind_name_eng}`,
      ind_code: ind.ind_code,
      ind_name_eng: ind.ind_name_eng,
      dim1: null,
    }));

    const existingKeys = new Set(options.map(o => o.key));
    mappings.forEach(m => {
      if (m.oic_ind_code && m.dim1) {
        const key = `${m.oic_ind_code}|${m.dim1}`;
        if (!existingKeys.has(key)) {
          options.push({
            key,
            value: String(m.oic_ind_code),
            label: `${m.oic_ind_code} - ${m.oic_ind_name || ''} [${m.dim1}]`,
            ind_code: m.oic_ind_code,
            ind_name_eng: m.oic_ind_name,
            dim1: m.dim1,
          });
          existingKeys.add(key);
        }
      }
    });

    return options.sort((a, b) => (a.ind_name_eng || '').localeCompare(b.ind_name_eng || ''));
  }, [availableIndicators, mappings]);

  useEffect(() => {
    if (visible) {
      fetchMappings();
      fetchWhoItems('');
      setWhoSearch('');
    }
  }, [visible]);

  const fetchMappings = async () => {
    try {
      const res = await AxiosOICStat.get('/external_api/who/indicator-mappings');
      setMappings(res.data?.mappings || res.data || []);
    } catch (e) {
      // ignore
    }
  };

  const fetchWhoItems = async (search = '') => {
    try {
      const q = [];
      if (typeof search === 'string' && search.trim()) q.push(`search=${encodeURIComponent(search.trim())}`);
      q.push('limit=100');
      const url = `/external_api/who/get-all-who-data?${q.join('&')}`;
      const res = await AxiosOICStat.get(url);
      const items = res.data || res.data?.items || [];
      const normalized = Array.isArray(items) ? items : [];
      const currentComposite = form.getFieldValue('who_indicator_code');
      if (currentComposite && String(currentComposite).includes('||')) {
        const [curCode, curDim] = String(currentComposite).split('||');
        const exists = normalized.find(i => String(i.who_indicator_code) === String(curCode) && String(i.dim1 || '') === String(curDim || ''));
        if (!exists) {
          const fallback = {
            who_indicator_code: curCode,
            who_indicator_name: form.getFieldValue('who_indicator_name') || null,
            who_topic: form.getFieldValue('who_topic') || null,
            dim1: curDim || undefined,
          };
          const s = String(search || '').trim().toLowerCase();
          const fallbackMatches = !s || String(curCode || '').toLowerCase().includes(s) || String(fallback.who_indicator_name || '').toLowerCase().includes(s);
          if (fallbackMatches) normalized.unshift(fallback);
        }
      }

      setWhoItems(normalized);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchWhoItems(whoSearch || '');
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [whoSearch]);

  useEffect(() => {
    if (visible && record) {
      form.setFieldsValue({
        oic_ind_code: record.oic_ind_code ? String(record.oic_ind_code) : undefined,
        oic_ind_name: record.oic_ind_name,
        who_indicator_code: record.who_indicator_code ? `${record.who_indicator_code}||${record.dim1 || ''}` : undefined,
        who_topic: record.who_topic,
        who_indicator_name: record.who_indicator_name,
        dim1: record.dim1 || undefined,
      });

      if (record.who_indicator_code) {
        setWhoItems(prev => {
          const exists = prev.find(i => String(i.who_indicator_code) === String(record.who_indicator_code) && String(i.dim1 || '') === String(record.dim1 || ''));
          if (exists) return prev;
          const item = { who_indicator_code: record.who_indicator_code, who_indicator_name: record.who_indicator_name, who_topic: record.who_topic, dim1: record.dim1 };
          return [item, ...prev];
        });
      }
    }

    if (!visible && !record) form.resetFields();
  }, [visible, record, form]);


  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (values.who_indicator_code && String(values.who_indicator_code).includes('||')) {
        const parts = String(values.who_indicator_code).split('||');
        values.who_indicator_code = parts[0] || '';
        values.dim1 = values.dim1 || (parts[1] || '') || undefined;
      }
      setLoading(true);
      if (record && record.id) {
        await AxiosOICStat.put(`/external_api/who/indicator-mappings/${record.id}`, values);
        message.success('Indicator mapping updated');
      } else {
        await AxiosOICStat.post('/external_api/who/indicator-mappings', values);
        message.success('Indicator mapping created');
      }
      onSuccess();
    } catch (err) {
      if (err.errorFields) return;
      const detail = err?.response?.data?.detail || err?.message || 'Failed to save mapping';
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  // simple uniqueness validators
  const validateUnique = (field, val) => {
    if (!val) return true;
    const compareVal = String(val).includes('||') ? String(val).split('||')[0] : String(val);
    return !mappings.some(m => String(m[field] || '') === compareVal && (!record || String(m.id) !== String(record.id)));
  };

  return (
    <Modal
      title={record ? 'Edit WHO Indicator Mapping' : 'Add WHO Indicator Mapping'}
      visible={visible}
      onOk={handleSubmit}
      onCancel={() => { form.resetFields(); onCancel(); }}
      confirmLoading={loading}
      width={600}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="oic_ind_code"
          label="OIC Indicator"
          rules={[{ required: true, message: 'Please select an OIC indicator' }]}
        >
          <Select
            showSearch
            autoClearSearchValue={false}
            allowClear
            placeholder="Select an indicator"
            onChange={(val) => {
              const sel = indicatorOptions.find(i => String(i.value) === String(val));
              form.setFieldsValue({
                oic_ind_name: sel?.ind_name_eng || null,
              });
            }}
            optionFilterProp="children"
            filterOption={(input, option) => {
              const label = option?.children || '';
              return String(label).toLowerCase().includes(input.toLowerCase());
            }}
          >
            {indicatorOptions.map(ind => (
              <Option key={ind.key} value={ind.value}>{ind.label}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="oic_ind_name" style={{ display: 'none' }}>
          <Input />
        </Form.Item>

        <Form.Item
          name="who_indicator_code"
          label="WHO Indicator"
          rules={[{ required: true, message: 'Please select a WHO indicator' }, { validator: (_, val) => {
            if (!val) return Promise.resolve();
            if (!validateUnique('who_indicator_code', val)) return Promise.reject(new Error('WHO indicator code already mapped'));
            return Promise.resolve();
          } }]}
        >
          <Select
            showSearch
            autoClearSearchValue={false}
            allowClear
            placeholder="Select a WHO indicator"
            optionFilterProp="children"
            filterOption={false}
            onSearch={(val) => setWhoSearch(val)}
            onChange={(val) => {
              const parts = String(val || '').split('||');
              const code = parts[0] || '';
              const dim = parts[1] || '';
              const sel = whoItems.find(i => String(i.who_indicator_code) === String(code) && String(i.dim1 || '') === String(dim));
              if (sel) {
                form.setFieldsValue({
                  who_indicator_code: val,
                  who_topic: sel.who_topic || null,
                  who_indicator_name: sel.who_indicator_name || null,
                  dim1: sel.dim1 || undefined,
                });
              } else {
                form.setFieldsValue({ who_indicator_code: val, who_topic: null, who_indicator_name: null, dim1: undefined });
              }
            }}
          >
              {whoItems.map((it, idx) => {
                const namePart = it.who_indicator_name ? ` - ${it.who_indicator_name}` : '';
                const dim1Label = it.dim1 ? ` [${it.dim1}]` : '';
                const optionValue = `${it.who_indicator_code}||${it.dim1 || ''}`;
                return (
                  <Option key={`${it.who_indicator_code}_${it.dim1 || 'none'}_${idx}`} value={optionValue}>
                    {it.who_indicator_code + namePart}{dim1Label}
                  </Option>
                );
              })}
          </Select>
        </Form.Item>

        <Form.Item name="who_topic" style={{ display: 'none' }}>
          <Input />
        </Form.Item>

        <Form.Item name="who_indicator_name" style={{ display: 'none' }}>
          <Input />
        </Form.Item>

        <Form.Item name="dim1" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default WhoIndicatorMappingModal;